import React, { useState, useEffect, useCallback } from 'react';
import BasicInfo from './BasicInfo';
import StampSettings from './StampSettings';
import StoreUsers from './StoreUsers';
import { useConfirm } from '../../contexts/ConfirmContext';
import { apiFetch } from '../../utils/apiClient';

const Settings = ({ storeInfo, onStoreInfoUpdate }) => {
  const confirm = useConfirm();
  const [settings, setSettings] = useState({
    storeName: storeInfo.storeName,
    storeCode: storeInfo.storeCode,
    stampRule: storeInfo.stampRule || 10,
    couponMessage: "축하합니다! 카페 브라운에서 무료 음료 1잔을 드립니다.",
    openTime: "09:00",
    closeTime: "22:00",
    isActive: true,
    // 같은 번호 재적립 제한 시간(0~24, 0=제한없음) - 실제로 서버
    // AddStamp가 이 값을 쓴다(stores.stamp_reset_hours). fetchSettings로
    // 실제 값을 불러오기 전까지는 서버 기본값(2)으로 맞춰둔다.
    stampResetHours: 2,
    stampRules: [],
    visitMilestones: []
  });
  const [storeUsers, setStoreUsers] = useState([]);

  const [isSaving, setIsSaving] = useState(false);
  const [rulesError, setRulesError] = useState('');
  const [milestonesError, setMilestonesError] = useState('');
  const [usersError, setUsersError] = useState('');
  const [settingsError, setSettingsError] = useState('');

  const fetchSettings = useCallback(async () => {
    try {
      const response = await apiFetch(
        `/api/settings?store_id=${encodeURIComponent(storeInfo.id)}`
      );
      const result = await response.json();
      if (result.success && result.data) {
        setSettings((prev) => ({
          ...prev,
          storeName: result.data.store_name,
          isActive: result.data.is_active,
          stampResetHours: result.data.stamp_reset_hours,
        }));
      }
    } catch (error) {
      setSettingsError('설정을 불러오지 못했습니다.');
    }
  }, [storeInfo.id]);

  const fetchStampRules = useCallback(async () => {
    try {
      const response = await apiFetch(
        `/api/settings/stamp-rules?store_id=${encodeURIComponent(storeInfo.id)}`
      );
      const result = await response.json();
      if (result.success) {
        const stampRules = (result.data || []).map((rule) => ({
          id: rule.id,
          requiredStamps: rule.required_stamps,
          couponType: rule.coupon_type,
          expiryDays: rule.expiry_days
        }));
        setSettings((prev) => ({ ...prev, stampRules }));
      }
    } catch (error) {
      setRulesError('스탬프 규칙을 불러오지 못했습니다.');
    }
  }, [storeInfo.id]);

  const fetchVisitMilestones = useCallback(async () => {
    try {
      const response = await apiFetch(
        `/api/settings/visit-milestones?store_id=${encodeURIComponent(storeInfo.id)}`
      );
      const result = await response.json();
      if (result.success) {
        const visitMilestones = (result.data || []).map((milestone) => ({
          id: milestone.id,
          visitCount: milestone.visit_count,
          couponType: milestone.coupon_type,
          expiryDays: milestone.expiry_days
        }));
        setSettings((prev) => ({ ...prev, visitMilestones }));
      }
    } catch (error) {
      setMilestonesError('방문 마일스톤을 불러오지 못했습니다.');
    }
  }, [storeInfo.id]);

  const fetchStoreUsers = useCallback(async () => {
    try {
      const response = await apiFetch(
        `/api/settings/store-users?store_id=${encodeURIComponent(storeInfo.id)}`
      );
      const result = await response.json();
      if (result.success) {
        const users = (result.data || []).map((user) => ({
          id: user.id,
          loginId: user.login_id,
          name: user.name,
          phone: user.phone
        }));
        setStoreUsers(users);
      }
    } catch (error) {
      setUsersError('계정 목록을 불러오지 못했습니다.');
    }
  }, [storeInfo.id]);

  useEffect(() => {
    fetchSettings();
    fetchStampRules();
    fetchVisitMilestones();
    fetchStoreUsers();
  }, [fetchSettings, fetchStampRules, fetchVisitMilestones, fetchStoreUsers]);

  const handleSettingsChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddStampRule = async (requiredStamps, couponType, expiryDays) => {
    const expiryText = expiryDays == null ? '무제한' : `${expiryDays}일`;
    if (!(await confirm(`스탬프 ${requiredStamps}개 → "${couponType}" (유효기간 ${expiryText}) 규칙을 추가하시겠습니까?`))) return;
    setRulesError('');
    try {
      const response = await apiFetch(
        `/api/settings/stamp-rules?store_id=${encodeURIComponent(storeInfo.id)}`,
        {
          method: 'POST',
          body: JSON.stringify({ required_stamps: requiredStamps, coupon_type: couponType, expiry_days: expiryDays })
        }
      );
      const result = await response.json();
      if (!result.success) {
        setRulesError(result.message || '스탬프 규칙 추가에 실패했습니다.');
        return;
      }
      await fetchStampRules();
    } catch (error) {
      setRulesError('네트워크 오류가 발생했습니다.');
    }
  };

  const handleRemoveStampRule = async (id) => {
    const rule = (settings.stampRules || []).find((r) => r.id === id);
    const detail = rule ? `스탬프 ${rule.requiredStamps}개 → "${rule.couponType}"` : '이 스탬프 규칙';
    if (!(await confirm(`${detail} 규칙을 삭제하시겠습니까?`))) return;
    setRulesError('');
    try {
      const response = await apiFetch(
        `/api/settings/stamp-rules/${id}?store_id=${encodeURIComponent(storeInfo.id)}`,
        { method: 'DELETE' }
      );
      const result = await response.json();
      if (!result.success) {
        setRulesError(result.message || '스탬프 규칙 삭제에 실패했습니다.');
        return;
      }
      await fetchStampRules();
    } catch (error) {
      setRulesError('네트워크 오류가 발생했습니다.');
    }
  };

  const handleAddVisitMilestone = async (visitCount, couponType, expiryDays) => {
    const expiryText = expiryDays == null ? '무제한' : `${expiryDays}일`;
    if (!(await confirm(`누적 방문 ${visitCount}회 → "${couponType}" (유효기간 ${expiryText}) 마일스톤을 추가하시겠습니까?`))) return;
    setMilestonesError('');
    try {
      const response = await apiFetch(
        `/api/settings/visit-milestones?store_id=${encodeURIComponent(storeInfo.id)}`,
        {
          method: 'POST',
          body: JSON.stringify({ visit_count: visitCount, coupon_type: couponType, expiry_days: expiryDays })
        }
      );
      const result = await response.json();
      if (!result.success) {
        setMilestonesError(result.message || '방문 마일스톤 추가에 실패했습니다.');
        return;
      }
      await fetchVisitMilestones();
    } catch (error) {
      setMilestonesError('네트워크 오류가 발생했습니다.');
    }
  };

  const handleRemoveVisitMilestone = async (id) => {
    const milestone = (settings.visitMilestones || []).find((m) => m.id === id);
    const detail = milestone ? `누적 방문 ${milestone.visitCount}회 → "${milestone.couponType}"` : '이 방문 마일스톤';
    if (!(await confirm(`${detail} 마일스톤을 삭제하시겠습니까?`))) return;
    setMilestonesError('');
    try {
      const response = await apiFetch(
        `/api/settings/visit-milestones/${id}?store_id=${encodeURIComponent(storeInfo.id)}`,
        { method: 'DELETE' }
      );
      const result = await response.json();
      if (!result.success) {
        setMilestonesError(result.message || '방문 마일스톤 삭제에 실패했습니다.');
        return;
      }
      await fetchVisitMilestones();
    } catch (error) {
      setMilestonesError('네트워크 오류가 발생했습니다.');
    }
  };

  const handleAddStoreUser = async (loginId, name, phone) => {
    if (!(await confirm(`계정 "${loginId}"(${name})을(를) 추가하시겠습니까?\n초기 비밀번호는 1234입니다.`))) return;
    setUsersError('');
    try {
      const response = await apiFetch(
        `/api/settings/store-users?store_id=${encodeURIComponent(storeInfo.id)}`,
        {
          method: 'POST',
          body: JSON.stringify({ login_id: loginId, name, phone })
        }
      );
      const result = await response.json();
      if (!result.success) {
        setUsersError(result.message || '계정 추가에 실패했습니다.');
        return;
      }
      await fetchStoreUsers();
    } catch (error) {
      setUsersError('네트워크 오류가 발생했습니다.');
    }
  };

  const handleRemoveStoreUser = async (id) => {
    const user = (storeUsers || []).find((u) => u.id === id);
    const detail = user ? `계정 "${user.loginId}"(${user.name})` : '이 계정';
    if (!(await confirm(`${detail}을(를) 삭제하시겠습니까?`))) return;
    setUsersError('');
    try {
      const response = await apiFetch(
        `/api/settings/store-users/${id}?store_id=${encodeURIComponent(storeInfo.id)}`,
        { method: 'DELETE' }
      );
      const result = await response.json();
      if (!result.success) {
        setUsersError(result.message || '계정 삭제에 실패했습니다.');
        return;
      }
      await fetchStoreUsers();
    } catch (error) {
      setUsersError('네트워크 오류가 발생했습니다.');
    }
  };

  // 예전엔 "설정 저장" 버튼 하나가 페이지 맨 아래 있어서, 스탬프 규칙/
  // 계정처럼 다른 항목들은 다 즉시 반영되는데 이것들(매장명/재적립
  // 제한시간)만 따로 저장을 눌러야 해서 헷갈렸다. 이제 각 필드 옆에
  // 붙은 작은 "저장" 버튼이 confirmMessage로 확인창을 띄운 뒤 바로
  // 반영한다 - 다른 즉시-반영 항목들과 같은 패턴.
  //
  // overrides를 받는 이유: 예를 들어 재적립 제한 시간 입력칸은 "값을
  // 확정하고(commitResetHours) 그 결과를 바로 저장 버튼에 넘기는" 흐름을
  // 쓰는데, setSettings는 비동기라 같은 클릭 핸들러 안에서 곧바로
  // handleSave를 부르면 settings.stampResetHours가 아직 새 값으로
  // 안 바뀐 상태(리렌더 전)일 수 있다. overrides로 값을 직접 넘기면
  // React state 갱신 타이밍과 무관하게 항상 정확한 값을 저장한다.
  const handleSave = async (confirmMessage, overrides = {}) => {
    if (confirmMessage && !(await confirm(confirmMessage))) {
      return;
    }
    setIsSaving(true);
    setSettingsError('');
    try {
      const response = await apiFetch(
        `/api/settings?store_id=${encodeURIComponent(storeInfo.id)}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            store_name: overrides.storeName ?? settings.storeName,
            is_active: overrides.isActive ?? settings.isActive,
            stamp_reset_hours: overrides.stampResetHours ?? settings.stampResetHours,
          }),
        }
      );
      const result = await response.json();
      if (!result.success) {
        setSettingsError(result.message || '설정 저장에 실패했습니다.');
        return;
      }
      onStoreInfoUpdate && onStoreInfoUpdate(settings);
    } catch (error) {
      setSettingsError('네트워크 오류가 발생했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">매장 설정</h2>

      {/* 기본 정보 - BasicInfo 컴포넌트 사용 */}
      <BasicInfo
        settings={settings}
        onSettingsChange={handleSettingsChange}
        onSave={handleSave}
        isSaving={isSaving}
      />

      {settingsError && (
        <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3">{settingsError}</div>
      )}
      {rulesError && (
        <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3">{rulesError}</div>
      )}
      {milestonesError && (
        <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3">{milestonesError}</div>
      )}

      {/* 스탬프 및 쿠폰 설정 - StampSettings 컴포넌트 사용 */}
      <StampSettings
        settings={settings}
        onSettingsChange={handleSettingsChange}
        onAddStampRule={handleAddStampRule}
        onRemoveStampRule={handleRemoveStampRule}
        onAddVisitMilestone={handleAddVisitMilestone}
        onRemoveVisitMilestone={handleRemoveVisitMilestone}
        onSaveResetHours={handleSave}
        isSaving={isSaving}
      />

      {usersError && (
        <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3">{usersError}</div>
      )}

      {/* 관리자 웹 로그인 계정 - StoreUsers 컴포넌트 사용 */}
      <StoreUsers
        storeUsers={storeUsers}
        onAdd={handleAddStoreUser}
        onRemove={handleRemoveStoreUser}
        isOwner={!!storeInfo.is_owner}
      />

      {/* 영업 시간 - OperatingHours 컴포넌트 사용 */}
      {/* <OperatingHours
        settings={settings}
        onSettingsChange={handleSettingsChange}
      /> */}
    </div>
  );
};

export default Settings;
